// hooks/market/useSubscriptions.ts
//
// Gère les abonnements de l'utilisateur connecté aux boutiques. Deux
// usages distincts, comme useFavorites.ts :
//   - isSubscribed(shopId) + toggleSubscribe(shopId) : utilisés par
//     ShopHeader (bouton s'abonner/se désabonner) sur
//     app/(tabs)/market/boutiques/[slug].tsx, avec mise à jour optimiste.
//   - subscribedShops (liste complète avec détails boutique) +
//     loadMore/refresh : utilisés par la section "Boutiques abonnées"
//     de reglages.tsx.
//
// Appelle le vrai backend :
//   POST {API_BASE_URL}/market   action: 'get-subscriptions' (juste les shop_id, pour isSubscribed)
//   POST {API_BASE_URL}/market   action: 'get-subscribed-shops' (détails complets, paginé)
//   POST {API_BASE_URL}/market   action: 'toggle-subscription', { shop_id, subscribed }
// (voir backend/handleMarket.ts → getSubscriptions / getSubscribedShops / toggleSubscription)

import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';
import type { MarketShopListItem } from '@/hooks/market/useShopsList';

const PAGE_SIZE = 20;

export function useSubscriptions() {
  // ── Set des shop_id suivis (pour isSubscribed/toggle rapide, sans
  // devoir charger la liste complète des détails à chaque fois) ──────
  const [subscribedShopIds, setSubscribedShopIds] = useState<Set<string>>(new Set());
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // ── Liste complète avec détails boutique, pour l'écran Réglages ─────
  const [subscribedShops, setSubscribedShops] = useState<MarketShopListItem[]>([]);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef  = useRef(0);
  const loadingRef = useRef(false);

  const fetchSubscribedShops = useCallback(async (mode: 'initial' | 'more') => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    if (mode === 'initial') setLoading(true);
    if (mode === 'more')    setLoadingMore(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const currentOffset = mode === 'more' ? offsetRef.current : 0;

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-subscribed-shops',
          access_token: token,
          limit:        PAGE_SIZE,
          offset:       currentOffset,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger les boutiques suivies');
        return;
      }

      const newShops: MarketShopListItem[] = data.shops || [];

      if (mode === 'more') {
        setSubscribedShops(prev => {
          const existingIds = new Set(prev.map(s => s.shop_id));
          return [...prev, ...newShops.filter(s => !existingIds.has(s.shop_id))];
        });
      } else {
        setSubscribedShops(newShops);
        // Synchronise le Set à partir de la première page — pour les
        // pages suivantes, subscribedShopIds ne fait que grossir via
        // toggle et via l'appel séparé get-subscriptions ci-dessous.
        setSubscribedShopIds(new Set(newShops.map(s => s.shop_id)));
      }

      offsetRef.current = data.next_offset ?? (currentOffset + newShops.length);
      setHasMore(Boolean(data.has_more));
    } catch (err) {
      console.error('[useSubscriptions]', err);
      setError('network_error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, []);

  // Appel séparé et léger (juste les shop_id) — utilisé au premier
  // montage pour que isSubscribed() réponde correctement même sur un
  // écran boutique/[slug].tsx qui ne charge jamais la liste complète
  // paginée ci-dessus (ex: on arrive directement sur une boutique via
  // un lien partagé, sans être jamais passé par la liste des abonnements).
  const fetchSubscriptionIds = useCallback(async () => {
    try {
      const token = await getValidToken();
      if (!token) return;

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-subscriptions',
          access_token: token,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setSubscribedShopIds(new Set(data.shop_ids || []));
      }
    } catch (err) {
      console.error('[useSubscriptions][ids]', err);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionIds();
  }, [fetchSubscriptionIds]);

  // CORRECTIF : sans cet effet, fetchSubscribedShops('initial') n'était
  // jamais déclenché automatiquement. Le state `loading` restait donc
  // bloqué à `true` indéfiniment (spinner infini dans reglages.tsx),
  // puisque seul le `finally` de fetchSubscribedShops le repasse à
  // `false` — et cette fonction n'était appelée nulle part au montage.
  useEffect(() => {
    fetchSubscribedShops('initial');
  }, [fetchSubscribedShops]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current) return;
    fetchSubscribedShops('more');
  }, [hasMore, fetchSubscribedShops]);

  const refresh = useCallback(() => {
    offsetRef.current = 0;
    fetchSubscribedShops('initial');
  }, [fetchSubscribedShops]);

  const isSubscribed = useCallback(
    (shopId: string) => subscribedShopIds.has(shopId),
    [subscribedShopIds]
  );

  const toggleSubscribe = useCallback(async (shopId: string) => {
    const currentlySubscribed = subscribedShopIds.has(shopId);
    const nextSubscribed = !currentlySubscribed;

    // Mise à jour optimiste — l'UI réagit immédiatement, sans attendre
    // la réponse du serveur.
    setSubscribedShopIds(prev => {
      const next = new Set(prev);
      if (nextSubscribed) next.add(shopId);
      else next.delete(shopId);
      return next;
    });
    setTogglingId(shopId);

    try {
      const token = await getValidToken();
      if (!token) {
        // Rollback si pas de session valide
        setSubscribedShopIds(prev => {
          const next = new Set(prev);
          if (nextSubscribed) next.delete(shopId);
          else next.add(shopId);
          return next;
        });
        return;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'toggle-subscription',
          access_token: token,
          shop_id:      shopId,
          subscribed:   nextSubscribed,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        // Rollback si le serveur refuse (ex: policy RLS qui bloque
        // l'auto-abonnement à sa propre boutique)
        setSubscribedShopIds(prev => {
          const next = new Set(prev);
          if (nextSubscribed) next.delete(shopId);
          else next.add(shopId);
          return next;
        });
      }
    } catch (err) {
      console.error('[useSubscriptions][toggle]', err);
      // Rollback réseau
      setSubscribedShopIds(prev => {
        const next = new Set(prev);
        if (nextSubscribed) next.delete(shopId);
        else next.add(shopId);
        return next;
      });
    } finally {
      setTogglingId(null);
    }
  }, [subscribedShopIds]);

  return {
    // Pour ShopHeader (bouton s'abonner)
    isSubscribed,
    toggleSubscribe,
    togglingId,
    // Pour l'écran Réglages (liste complète)
    subscribedShops,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
  };
}
